/**
 * The bar along the bottom: say or type anything.
 *
 * ## Why it is one field and not a menu
 *
 * The player's hands are on the keys. Every affordance this bar replaces — a rewind
 * button, a save button, a lens picker, a "why?" button — is a thing that would have to
 * be found, aimed at and tapped, and the graph is the only thing that should be competing
 * for the screen. One quiet pill accepts "take me back two chords", "save that", "what key
 * are we in?", "simpler" and everything else, and `commands/localCommands.ts` decides
 * whether that needs the network (mostly: no).
 *
 * ## Hold to talk, not always listening
 *
 * The mic glyph is held, not tapped-on-and-off, because always-on dictation beside an
 * acoustic piano transcribes the piano — and because iOS will not run `SpeechRecognition`
 * and our own capture at the same time, so the microphone has to be handed over and handed
 * back around exactly one utterance. `commands/voice.ts` does that; `recognizerControls`
 * is what it hands over, and On the Go it is `null`, which makes the whole dance a no-op
 * without a single branch on the mode.
 *
 * Where the platform has no speech recogniser the button is simply absent. Typing and
 * speaking are equivalent here, so a missing microphone costs the player nothing and does
 * not warrant an explanation.
 */

import { useState } from 'react';
import { CommandToast } from '../system/CommandToast.js';
import { DebugSheet } from '../sheets/DebugSheet.js';
import { SettingsDrawer } from '../sheets/SettingsDrawer.js';
import { useSessionView } from '../system/sessionView.js';
import { useCommandBar, type UseCommandBarOptions } from './useCommandBar.js';
import './commandbar.css';

export interface CommandBarProps extends UseCommandBarOptions {
  placeholder?: string;
}

export function CommandBar({
  placeholder = 'say or type anything…',
  ...options
}: CommandBarProps = {}): React.JSX.Element {
  const view = useSessionView(options.view);
  const bar = useCommandBar(options);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  // Both, and the second one matters: somebody who turned the prompt sheet on and then
  // switched the companion off would otherwise keep a button that opens an empty log.
  const showPrompts = (view.settings?.showPrompts ?? false) && view.settings?.aiCompanion !== false;

  return (
    <div className="hc-cmd" data-testid="hc-command-bar">
      <CommandToast message={bar.message} onDismiss={bar.dismissMessage} />

      <div className="hc-cmd__pill" data-busy={bar.busy ? 'true' : 'false'}>
        <input
          className="hc-cmd__input"
          data-testid="hc-command-input"
          type="text"
          inputMode="text"
          enterKeyHint="send"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="sentences"
          spellCheck={false}
          aria-label="Say or type a command"
          placeholder={placeholder}
          value={bar.text}
          onChange={(event) => bar.setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            // Prevents the soft keyboard's Go key from also submitting an enclosing form.
            event.preventDefault();
            void bar.submit();
          }}
        />

        {bar.voiceSupported ? (
          <button
            type="button"
            className={`hc-cmd__mic${bar.talking ? ' is-talking' : ''}`}
            data-testid="hc-command-mic"
            aria-label="Hold to talk"
            aria-pressed={bar.talking}
            onPointerDown={() => void bar.startTalking()}
            onPointerUp={() => bar.stopTalking()}
            // A finger sliding off the button is a release, not an abandonment: the
            // phrase was still spoken.
            onPointerLeave={() => bar.stopTalking()}
            onPointerCancel={() => bar.cancelTalking()}
            onKeyDown={(event) => {
              if (event.key !== ' ' && event.key !== 'Enter') return;
              // Otherwise the browser synthesises a click on release and starts a second
              // utterance on top of the one that is finishing.
              event.preventDefault();
              if (!event.repeat) void bar.startTalking();
            }}
            onKeyUp={(event) => {
              if (event.key === ' ' || event.key === 'Enter') bar.stopTalking();
            }}
          >
            <MicGlyph />
          </button>
        ) : null}

        {/*
          Only while the setting is on, and deliberately a word rather than another glyph:
          a fourth icon in the pill would be one more thing to decode for everybody, to
          serve the few sittings where somebody is looking under the bonnet. Off, this is
          not rendered and the sheet below is not mounted.
        */}
        {showPrompts ? (
          <button
            type="button"
            className="hc-cmd__prompts"
            data-testid="hc-command-prompts"
            aria-label="Model prompts"
            aria-expanded={debugOpen}
            onClick={() => setDebugOpen(true)}
          >
            prompts
          </button>
        ) : null}

        <button
          type="button"
          className="hc-cmd__gear"
          data-testid="hc-command-settings"
          aria-label="Settings"
          aria-expanded={settingsOpen}
          onClick={() => setSettingsOpen(true)}
        >
          <GearGlyph />
        </button>
      </div>

      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} view={view} />

      {showPrompts ? <DebugSheet open={debugOpen} onClose={() => setDebugOpen(false)} /> : null}
    </div>
  );
}

/** Inline rather than an asset: two glyphs are not worth a request or a font. */
function MicGlyph(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="hc-cmd__glyph">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6 11a6 6 0 0 0 12 0M12 17v4" fill="none" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function GearGlyph(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="hc-cmd__glyph">
      <circle cx="12" cy="12" r="3.2" fill="none" strokeWidth="1.8" />
      <path
        d="M12 2.6v2.2M12 19.2v2.2M21.4 12h-2.2M4.8 12H2.6M18.6 5.4l-1.6 1.6M7 17l-1.6 1.6M18.6 18.6 17 17M7 7 5.4 5.4"
        fill="none"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default CommandBar;
